import type { History } from '@compat/router';

interface Data {
  filter?: string;
  page: number;
  by?: string;
  last: string;
  sort: string;
  source: string;
  bludo: string;
  naznachenie: string;
  obrabotka: string;
  slozhnost: string;
  sauce: string;
  type: string;
  search: string;
  group?: string;
  identify: string;
  productPromo?: string;
}

/**
 * Значения /r/-параметров приезжают из pathname, то есть в процентном
 * энкодинге: `/recipes/r/search/%D0%B1%D0%BE%D1%80%D1%89`. Раскодируем их
 * прямо при разборе — потребители (запрос к API, сравнение с id фильтров,
 * значение поля поиска) ждут человекочитаемую строку. Без этого прямой
 * заход по ссылке с кириллическим поиском уходил в API как «%D0%B1...»
 * и отдавал пустую выдачу, хотя при вводе с клавиатуры (push кладёт в
 * историю уже строку) тот же запрос работал.
 * Битую последовательность (`/r/search/%`) decodeURIComponent роняет
 * URIError — в этом случае отдаём значение как есть.
 */
const decodeParam = (value: string): string => {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

export const getURLProps = ({
  pathname
}: {
  pathname: History['location']['pathname'];
}): Data => {
  // const groups = new URLSearchParams(location.search)?.get('groups')?.split(',') || undefined;
  const defaultData: Data = {
    page: 1,
    by: undefined,
    last: '6',
    sort: 'Fresh',
    source: 'All',
    bludo: '0',
    naznachenie: '0',
    obrabotka: '0',
    slozhnost: '0',
    type: '0',
    search: '',
    filter: undefined,
    group: undefined,
    identify: '',
    productPromo: '0',
    sauce: '0'
  };
  const data: {
    filter?: string;
  } = {};

  if (!pathname.includes('/r/')) {
    return {
      ...defaultData,
      ...data
    };
  }
  const slicedURI = pathname.split('/r/')[1].split('/');
  for (let i = 0; i < slicedURI.length; i += 2) {
    if (slicedURI[i] === 'promo') {
      data.filter = 'promo';
    } else if (slicedURI[i] === 'newbee') {
      data.filter = 'newbee';
    } else if (slicedURI[i] === 'delikateska') {
      data.filter = 'delikateska';
    } else {
      const value = slicedURI[i + 1];
      // хвост без значения (`/r/bludo`) оставляем как было — undefined
      data[slicedURI[i]] = value === undefined ? value : decodeParam(value);
    }
  }
  return {
    ...defaultData,
    ...data
  };
};

export const nextUrlPage = (
  pathname: History['location']['pathname'],
  search?: string
): string => {
  if (pathname.split('/page/')[1]) {
    return (
      `${pathname.split('/page/')[0]}/page/${
        +getURLProps({ pathname }).page + 1
      }` + (search ?? '')
    );
  }
  return (
    `${pathname + (pathname.split('/r/')[1] ? '' : '/r')}/page/${
      +getURLProps({ pathname }).page + 1
    }` + (search ?? '')
  );
};

export const setNewURLMoreParams = (location: any, ...args: any[]): string => {
  const newPathname = setNewURL(location, args[0], args[1]);
  const newArgs = args.slice(2);

  if (newArgs.length) {
    return setNewURLMoreParams(newPathname, ...newArgs);
  } else {
    return newPathname;
  }
};

export const setNewURL = (
  location: History['location'] | string,
  param: string,
  id: string,
  withPromoProduct?: boolean
): string => {
  const oldPathname =
    typeof location === 'string' ? location : location.pathname;

  const data: Record<string, string> = {};

  if (!oldPathname.includes('/r/') && id !== '0' && id !== '') {
    return `${oldPathname}/r/${param}/${id}`;
  } else if (!oldPathname.includes('/r/') && (id === '0' || id === '')) {
    return oldPathname;
  }
  const slicedURI = oldPathname.split('/r/')[1].split('/');
  for (let i = 0; i < slicedURI.length; i += 2) {
    data[slicedURI[i]] = slicedURI[i + 1];
  }
  if (id === '0' || id === '') {
    // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
    delete data[param];
  } else {
    data[param] = id;
  }

  delete data.page;
  if (!withPromoProduct && data.productPromo) {
    delete data.productPromo;
  }
  if (Object.keys(data).length === 0) {
    return oldPathname.split('/r/')[0];
  }
  let newPath = `${oldPathname.split('/r/')[0]}/r`;
  Object.keys(data).forEach(param => {
    newPath = `${newPath}/${param}/${data[param]}`;
  });

  return newPath;
};
